import { message } from 'antd';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { hasPrintDoc } from './print-registry';
import { fetchPrintView } from './print-api';
import { getPrintSize, setPrintSize } from './print-size';
import { usePrintStore } from './print-store';
import type { PrintDoc, PrintSize } from './print-types';

const READY_TIMEOUT_MS = 5000;
/** The layer is display:none on screen, so a late clear is harmless; it just must happen. */
const SAFETY_CLEAR_MS = 60_000;
/** An afterprint this soon after print() is a browser firing early, not the dialog closing. */
const EARLY_AFTERPRINT_MS = 500;

class NotReadyError extends Error {
  constructor() {
    super('print layer not ready');
    this.name = 'NotReadyError';
  }
}

// One print at a time, app-wide. Module-level so every usePrint() caller shares it.
let inFlight: Promise<void> | null = null;
let afterprintHandler: (() => void) | null = null;
let safetyTimer: ReturnType<typeof setTimeout> | null = null;

/** Takes the document off the print layer and drops every listener and timer tied to it. */
function endLayer(): void {
  if (afterprintHandler !== null) {
    window.removeEventListener('afterprint', afterprintHandler);
    afterprintHandler = null;
  }
  if (safetyTimer !== null) {
    clearTimeout(safetyTimer);
    safetyTimer = null;
  }
  usePrintStore.getState().clear();
}

function whenReady(): Promise<void> {
  return new Promise((resolve, reject) => {
    const done = (fn: () => void): void => {
      clearTimeout(timer);
      unsubscribe();
      fn();
    };
    const timer = setTimeout(() => {
      done(() => {
        reject(new NotReadyError());
      });
    }, READY_TIMEOUT_MS);
    const unsubscribe = usePrintStore.subscribe((s) => {
      if (s.ready) done(resolve);
    });
    if (usePrintStore.getState().ready) done(resolve);
  });
}

async function run(doc: PrintDoc, uid: string, size: PrintSize | undefined): Promise<void> {
  const data = await fetchPrintView(doc, uid);
  if (!hasPrintDoc(doc)) throw new Error(`no print layout registered for ${doc}`);
  endLayer();
  usePrintStore.getState().start({ doc, size: size ?? getPrintSize(doc), data });
  await whenReady();

  const calledAt = Date.now();
  // Kept mounted until the dialog closes: mobile browsers return from print()
  // at once, so clearing here would blank the preview.
  afterprintHandler = () => {
    if (Date.now() - calledAt < EARLY_AFTERPRINT_MS) return;
    endLayer();
  };
  window.addEventListener('afterprint', afterprintHandler);
  safetyTimer = setTimeout(endLayer, SAFETY_CLEAR_MS);
  window.print();
  if (size !== undefined) setPrintSize(doc, size);
}

export function usePrint(): {
  print: (doc: PrintDoc, uid: string, size?: PrintSize) => Promise<void>;
  printing: boolean;
} {
  const { t } = useTranslation();
  const [printing, setPrinting] = useState(false);

  const print = useCallback(
    (doc: PrintDoc, uid: string, size?: PrintSize): Promise<void> => {
      if (inFlight !== null) return inFlight;
      setPrinting(true);
      const current = run(doc, uid, size)
        .catch((e: unknown) => {
          endLayer();
          let text = t('print.failed');
          if (e instanceof ApiError) text = e.message;
          else if (e instanceof NotReadyError) text = t('print.notReady');
          void message.error(text);
        })
        .finally(() => {
          inFlight = null;
          setPrinting(false);
        });
      inFlight = current;
      return current;
    },
    [t],
  );

  return { print, printing };
}
